import {
  type ApiResponse,
  idempotencyHeader
} from '../../api-client';

import {
  apiRequest
} from './client';

import type {
  PracticePackCheckoutInput,
  PracticePackCheckoutResult,
  PracticePackEntitlement,
  PracticePackProgress,
  PracticePackProgressData,
  PracticePackQuote,
  PracticePackQuoteParams
} from './types/practice-packs.types';

// Practice packs (BMS feature 0861). Every call needs the signed-in user's
// Firebase token: the learner reads their own entitlements and progress, and
// a learner or a linked payer buys.

const packPath = (packKey: string) => `/practice-packs/${ encodeURIComponent(packKey) }`;

/** The caller's own ACTIVE, unexpired packs. The portal's practice route
 * calls this server-side, over BMS_API_BASE_URL_INTERNAL. */
export async function getMyPracticePackEntitlements(
  token: string
): Promise<ApiResponse<PracticePackEntitlement[]>> {

  return apiRequest<ApiResponse<PracticePackEntitlement[]>>('/practice-packs/entitlements/me', undefined, token);
}

/** The price to show (BMS feature 0902): list price, the referral discount
 * when one applies, and the verdict on a typed code. Same errors as checkout
 * (403, 409, 410), since it makes the same checks. */
export async function getPracticePackQuote(
  packKey: string,
  params: PracticePackQuoteParams,
  token: string
): Promise<ApiResponse<PracticePackQuote>> {

  const query = new URLSearchParams();

  if (params.learnerUserId !== undefined) {

    query.set('learnerUserId', String(params.learnerUserId));
  }

  if (params.referralCode) {

    query.set('referralCode', params.referralCode);
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';

  return apiRequest<ApiResponse<PracticePackQuote>>(`${ packPath(packKey) }/quote${ qs }`, undefined, token);
}

/** Starts a sale. 409 when the learner already has the pack; 403 when the
 * caller is not a linked payer of learnerUserId. */
export async function checkoutPracticePack(
  packKey: string,
  input: PracticePackCheckoutInput,
  token: string,
  idempotencyKey?: string
): Promise<ApiResponse<PracticePackCheckoutResult>> {

  return apiRequest<ApiResponse<PracticePackCheckoutResult>>(
    `${ packPath(packKey) }/checkout`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: idempotencyHeader(idempotencyKey)
    },
    token
  );
}

export async function getMyPracticePackProgress(
  packKey: string,
  token: string
): Promise<ApiResponse<PracticePackProgress>> {

  return apiRequest<ApiResponse<PracticePackProgress>>(`${ packPath(packKey) }/progress/me`, undefined, token);
}

/** 403 without an ACTIVE entitlement. */
export async function saveMyPracticePackProgress(
  packKey: string,
  progress: PracticePackProgressData,
  token: string
): Promise<ApiResponse<unknown>> {

  return apiRequest<ApiResponse<unknown>>(
    `${ packPath(packKey) }/progress/me`,
    {
      method: 'PUT',
      body: JSON.stringify({
        progress
      })
    },
    token
  );
}
